import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { OpsFeatureFlags } from "@/components/admin/OpsFeatureFlags";
import { useOpsFlags } from "@/hooks/useOpsFlags";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { listAvailability, setAvailability } from "@/lib/admin/ops.functions";
import {
  AVAILABILITY_ENTITIES,
  AVAILABILITY_LABEL,
  OPS_AVAILABILITY,
  type AvailabilityEntity,
  type OpsAvailability,
} from "@/lib/admin/ops.schemas";

export const Route = createFileRoute("/_admin/admin/availability")({
  head: () => ({
    meta: [
      { title: "Menu Availability — Admin" },
      {
        name: "description",
        content: "Set every menu item to Available, Sold out or Hidden across the storefront.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AvailabilityPage,
});

function badgeVariant(v: string): "default" | "secondary" | "outline" {
  if (v === "available") return "default";
  if (v === "sold_out") return "secondary";
  return "outline";
}

function AvailabilityPage() {
  const { flags, isLoading: flagsLoading } = useOpsFlags();
  const queryClient = useQueryClient();
  const [entity, setEntity] = useState<AvailabilityEntity>("mp2_dishes");
  const [search, setSearch] = useState("");

  const listFn = useServerFn(listAvailability);
  const setFn = useServerFn(setAvailability);
  const enabled = flags.availability_enabled;

  const rows = useQuery({
    queryKey: ["admin", "availability", entity, search],
    queryFn: () => listFn({ data: { entity, search } }),
    enabled,
    placeholderData: (prev) => prev,
  });

  const mutation = useMutation({
    mutationFn: ({ id, value }: { id: string; value: OpsAvailability }) =>
      setFn({ data: { entity, id, value } }),
    onSuccess: () => {
      toast.success("Availability updated");
      queryClient.invalidateQueries({ queryKey: ["admin", "availability"] });
    },
    onError: (err: Error) =>
      toast.error(
        err.message === "availability_disabled"
          ? "Availability control is turned off."
          : "Couldn't update availability.",
      ),
  });

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Availability"
        description="Sold-out items stay listed but can't be ordered. Hidden items disappear from the storefront."
      />

      {flagsLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !enabled ? (
        <div className="space-y-6">
          <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
            Availability control is turned off. Enable{" "}
            <span className="font-mono">AVAILABILITY_ENABLED</span> below.
          </div>
          <OpsFeatureFlags />
        </div>
      ) : (
        <>
          <Tabs
            value={entity}
            onValueChange={(v) => setEntity(v as AvailabilityEntity)}
          >
            <TabsList className="flex w-full flex-wrap">
              {AVAILABILITY_ENTITIES.map((e) => (
                <TabsTrigger key={e.key} value={e.key}>
                  {e.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items"
            aria-label="Search menu items"
          />

          {rows.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : rows.error ? (
            <p className="text-sm text-destructive">Couldn't load items.</p>
          ) : (rows.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No items found.</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {(rows.data ?? []).map((row) => (
                <li
                  key={row.id}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-3"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium">{row.name}</span>
                    <Badge
                      variant={badgeVariant(row.ops_availability)}
                      className="shrink-0"
                    >
                      {AVAILABILITY_LABEL[row.ops_availability as OpsAvailability] ??
                        row.ops_availability}
                    </Badge>
                  </div>
                  <Select
                    value={row.ops_availability}
                    disabled={mutation.isPending}
                    onValueChange={(v) =>
                      mutation.mutate({ id: row.id, value: v as OpsAvailability })
                    }
                  >
                    <SelectTrigger className="w-40 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPS_AVAILABILITY.map((v) => (
                        <SelectItem key={v} value={v}>
                          {AVAILABILITY_LABEL[v]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </li>
              ))}
            </ul>
          )}

          <OpsFeatureFlags />
        </>
      )}
    </div>
  );
}
